import {Plain} from '../../../../../shared/model/plain.type.model';
import {Choice} from './choice.model';
import {COMPARISON_WORDING} from '../conditions/comparison-wording.model';
import {CONDITION_OPERATORS} from '../conditions/condition-operators.model';
import {ConditionQuestionOption} from '../conditions/condition-question-option.model';
import {CONDITION_QUESTION_TYPES} from '../conditions/condition-question-types.model';
import {ConditionComparison} from '../conditions/condition-comparison.model';
import {ConditionValueOption} from '../conditions/condition-value-option.model';
import {CONDITION_VALUE_INPUT} from '../conditions/condition-value-input.model';
import {CES_RANGE} from './ces-range.model';
import {CES_RANGE_BOUNDS} from './ces-range-bounds.model';
import {CES_RANGES} from './ces-ranges.model';
import {CES_REPRESENTATIONS} from './ces-representations.model';
import {CES_TEXT_REPRESENTATION} from './ces-text-representation.model';
import {CES_TEXT_REPRESENTATIONS} from './ces-text-representations.model';
import {CHOICES_ANSWER_TYPE} from './choices-answer-type.model';
import {CHOICES_DISPLAY} from './choices-display.model';
import {CHOICES_LAYOUT} from './choices-layout.model';
import {ChoicesStyle} from './choices-style.model';
import {CONTENT_TYPE} from './content.type.model';
import {CSAT_RANGE} from './csat-range.model';
import {CSAT_RANGE_BOUNDS} from './csat-range-bounds.model';
import {CSAT_RANGES} from './csat-ranges.model';
import {DEFAULT_NPS_COLORS} from './nps-color-style.model';
import {DEFAULT_SCALE_RANGE} from './default-scale-range.model';
import {DEFAULT_SCALE_START} from './default-scale-start.model';
import {DisplayOptions} from '../conditions/display.options.model';
import {EFFORT_PALETTES} from './effort-palettes.model';
import {ElementConfiguration} from './element-configuration.model';
import {ElementStyle} from './element-style.model';
import {ELEMENT_TYPE} from './element.type.model';
import {EQUALITY_CONDITION_OPERATORS} from '../conditions/equality-condition-operators.model';
import {GRADUATED_COLORS, NEUTRAL_COLOR, RatedColorMode} from './rated-color-mode.model';
import {LABEL_KEY} from '../label-key.model';
import {LabelTranslator} from '../label-translator.model';
import {resolveLabelKeys} from '../resolve-label-keys.model';
import {PLATFORM_REVIEW_BUTTONS} from './platform-review-buttons.model';
import {PlatformReviewForm} from './platform-review-form.model';
import {QUESTION_TYPE} from './question.type.model';
import {RATED_QUESTION_TYPES} from './rated-question-types.model';
import {RATED_REPRESENTATIONS} from './rated-representations.model';
import {RATING_COLOR} from './rating-color.model';
import {RatingStyle} from './rating-style.model';
import {REPRESENTATION} from './representation.model';
import {SATISFACTION_PALETTES} from './satisfaction-palettes.model';
import {SCALE_REPRESENTATIONS} from './scale-representations.model';
import {NEUTRAL_SMILEY, SMILEY_PALETTES, STAR_ICON} from './smiley-palettes.model';
import {STAGED_QUESTION_TYPES} from './staged-question-types.model';
import {STAR_COLOR} from './star-color.model';
import {SubQuestion} from './sub-question.model';
import {TargetPlatformReview} from './target-platform-review.model';
import {TARGET_PLATFORM_REVIEWS} from './target-platform-reviews.model';
import {VISUAL_EMOJI} from './visual-emoji.model';

const CONTENT_TYPES: string[] = Object.values(CONTENT_TYPE);

/** Un indicateur se note de zéro à dix : onze crans, que son paramétrage ne règle pas — sauf la satisfaction et l'effort. */
const INDICATOR_STEP_COUNT = 11;

/** Un effort noté en toutes lettres se dit sur cinq encadrés, quelle que soit son étendue chiffrée. */
const EFFORT_TEXT_STEP_COUNT = 5;

/** Seuils de l'analyse d'une recommandation : détracteurs jusqu'à 6, passifs à 7 et 8, promoteurs ensuite. */
const NPS_LAST_DETRACTOR_STEP = 6;

const NPS_LAST_PASSIVE_STEP = 8;

/** Les crans d'une question notée se suivent d'un en un, depuis le premier. */
const stepsFrom = (first: number, count: number): number[] =>
    Array.from({length: Math.max(count, 0)}, (_, rank) => first + rank);

/** Couleurs d'une satisfaction pour une longueur d'échelle donnée ; onze crans à défaut de mieux. */
const satisfactionPalette = (count: number): string[] =>
    SATISFACTION_PALETTES[count] ?? SATISFACTION_PALETTES[INDICATOR_STEP_COUNT];

/** Couleurs d'un effort pour une longueur d'échelle donnée ; onze crans à défaut de mieux. */
const effortPalette = (count: number): string[] => EFFORT_PALETTES[count] ?? EFFORT_PALETTES[INDICATOR_STEP_COUNT];

/** Statuts d'un indicateur, tels que l'analyse les distingue ; le libellé est une clé de traduction. */
const statusValues = (prefix: string, statuses: string[]): ConditionValueOption[] =>
    statuses.map((status) => ({value: status, label: `label.${prefix}_status_${status.toLowerCase()}`}));

/**
 * Libellés d'une liste — choix, critères — dans la langue de composition.
 *
 * Le libellé est rangé sous `text`, comme le titre d'une question, et non à même l'entrée : le lire
 * à la racine renverrait vide sur tout ce qui vient du serveur.
 */
const listLabels = (items?: {text?: Record<string, string>}[]): string[] =>
    (items ?? []).map((item) => item.text?.[LABEL_KEY] ?? '');

/**
 * Réécrit les libellés d'une liste, un par entrée et dans le même ordre ; une entrée déjà là ne perd
 * rien — son identifiant et ses autres traductions traversent.
 *
 * Un libellé sans entrée en crée une : c'est ainsi que le premier choix d'une question qui n'en
 * portait aucun prend corps dans le brouillon, à la première frappe.
 */
const withListLabels = <T extends {id?: number | null; text?: Record<string, string>}>(
    items: T[] | undefined,
    labels: string[]
): T[] =>
    labels.map((label, rank) => {
        const item = (items ?? [])[rank];

        return {id: null, ...item, text: {...item?.text, [LABEL_KEY]: label}} as T;
    });

/**
 * Élément d'une page du questionnaire.
 *
 * Le backend renvoie une hiérarchie polymorphe discriminée par `type` — questions (`NPS`, `CSAT`,
 * `CES`, `MULTI_CSAT`, `COMMENT`…), zone de texte, lien vers un site d'avis — dont chaque variante
 * porte ses propres champs : libellés traduits, configuration, sous-questions, style…
 *
 * Ces variantes ne sont pas encore modélisées ici. En attendant, l'élément conserve tout ce qu'il
 * reçoit : sans cela, réenregistrer le brouillon après une simple modification d'habillage
 * renverrait des éléments vidés de leur contenu. L'index de secours disparaîtra quand chaque type
 * d'élément aura sa classe.
 *
 * Ce qu'il sait faire tient dans des méthodes de prototype, et non dans des champs-flèches : le
 * constructeur recopie ses données par `Object.assign`, qui écraserait les secondes — et recopier un
 * élément (`new ElementPage({...element, …})`, comme le fait le brouillon à chaque retouche) les
 * emporterait, restées liées à l'élément d'origine.
 */
export class ElementPage {
    [field: string]: unknown;

    /** `null` tant que le serveur n'a pas attribué le sien, à l'enregistrement du brouillon. */
    public id?: number | null;

    /** Discriminant de la variante : `NPS`, `CSAT`, `TEXT_ZONE`, `PLATFORM_REVIEW`… */
    public type?: ELEMENT_TYPE;

    /**
     * Étape du parcours sur laquelle porte la question, dans une enquête par étapes. Les questions
     * qui se mesurent étape par étape y sont rattachées ; les autres, et toutes celles des enquêtes
     * ordinaires, n'en ont pas.
     */
    public stageId?: number | null;

    /** Libellés par langue d'une question ; la description y reste facultative. */
    public title?: Record<string, string>;

    public description?: Record<string, string>;

    /** Ce qui varie d'un type à l'autre : bornes d'échelle, contraintes, choix proposés… */
    public configuration?: ElementConfiguration;

    /** Critères d'une question multicritères, notés chacun sur l'échelle de la question. */
    public subQuestions?: SubQuestion[];

    /** Choix offerts par une question à choix unique ou multiple. */
    public choices?: Choice[];

    /** Contenu enrichi d'une zone de texte, par langue : ce que l'éditeur compose et rend en HTML. */
    public message?: Record<string, string>;

    /** Plateforme visée par une invitation à déposer un avis. */
    public site?: TargetPlatformReview;

    /** Adresse de la page où le répondant déposera son avis. */
    public link?: string;

    public buttonFontColor?: string;

    public buttonBackgroundColor?: string;

    /** Le lien s'ouvre à côté du questionnaire plutôt qu'à sa place. */
    public openNewTab?: boolean;

    public displayOptions: DisplayOptions;

    constructor(data: Plain<ElementPage>) {
        Object.assign(this, data);
        this.displayOptions = new DisplayOptions(data.displayOptions as Plain<DisplayOptions> | undefined);
    }

    /**
     * Les conditions qui décident de l'affichage de cet élément sont-elles remplies ?
     *
     * Un élément conditionné par une condition à moitié composée ne s'afficherait jamais. Un élément
     * toujours affiché n'a rien à remplir.
     *
     * Méthode de prototype et non champ-flèche : le constructeur recopie les données reçues par
     * `Object.assign`, qui emporterait un champ portant le même nom.
     */
    public hasCompleteDisplayOptions(): boolean {
        return this.displayOptions.isComplete();
    }

    /** Vrai pour une question, qui porte alors un titre et une description. */
    public isQuestion(): boolean {
        return !!this.type && !CONTENT_TYPES.includes(this.type);
    }

    /**
     * Vrai pour une question sur laquelle une condition d'affichage peut porter, c'est-à-dire dont
     * les réponses s'énumèrent : sans valeurs à proposer, il n'y aurait rien à comparer.
     */
    public isConditionable(): boolean {
        return CONDITION_QUESTION_TYPES.includes(this.type as QUESTION_TYPE);
    }

    /**
     * Vrai pour une question dont la réponse est une note donnée sur une suite de crans. Elle se
     * présente alors encadrée de ses deux bornes, qu'elle soit un indicateur ou une échelle
     * d'évaluation.
     */
    public isRated(): boolean {
        return RATED_QUESTION_TYPES.includes(this.type as QUESTION_TYPE);
    }

    /**
     * Vrai pour une question qui se mesure étape par étape : elle doit alors dire de laquelle du
     * parcours elle parle.
     *
     * Le type seul le dit. Que la question ait effectivement une étape à désigner ne se sait pas
     * ici : c'est l'enquête, et non l'élément, qui procède par étapes ou non.
     */
    public isStaged(): boolean {
        return STAGED_QUESTION_TYPES.includes(this.type as QUESTION_TYPE);
    }

    /** Vrai pour une question à choix, seule à lister ce qu'elle propose. */
    public hasChoices(): boolean {
        return this.type === QUESTION_TYPE.CHOICES;
    }

    /** Vrai pour une recommandation, notée de zéro à dix sur une échelle qui ne se règle pas. */
    public isNps(): boolean {
        return this.type === QUESTION_TYPE.NPS;
    }

    /** Vrai pour un niveau d'effort : la peine que le répondant a dû se donner. */
    public isCes(): boolean {
        return this.type === QUESTION_TYPE.CES;
    }

    /**
     * Vrai pour une échelle d'évaluation : la seule question notée qui ne mesure aucun indicateur,
     * et dont l'étendue se règle donc librement plutôt que d'être imposée par l'analyse.
     */
    public isScale(): boolean {
        return this.type === QUESTION_TYPE.SCALE;
    }

    /**
     * Vrai d'une satisfaction, sous ses trois formes — globale, sur un critère, sur plusieurs.
     * Toutes trois se règlent de la même façon : c'est la même question, posée une fois ou plusieurs.
     */
    public isSatisfaction(): boolean {
        return (
            this.type === QUESTION_TYPE.CSAT ||
            this.type === QUESTION_TYPE.MONO_CSAT ||
            this.type === QUESTION_TYPE.MULTI_CSAT
        );
    }

    /** Vrai des questions dont les couleurs se règlent : les autres gardent celles de leur analyse. */
    public hasColorMode(): boolean {
        return this.isNps() || this.isCes() || this.isSatisfaction();
    }

    /**
     * Comment les crans se colorent : une couleur par classe de répondant, ou une seule pour toute
     * l'échelle.
     *
     * Le mode se lit dans le drapeau que porte l'habillage — `customColor` pour une recommandation,
     * `customColors` pour une satisfaction —, qui dit si les couleurs de l'élément priment sur celle
     * des réponses. Une question qui ne dit rien est graduée : c'est le cas par défaut, et c'est
     * ainsi que le serveur livre ses modèles.
     */
    public colorMode(): RatedColorMode {
        const custom = this.isNps()
            ? this.configuration?.style?.npsColor?.customColor
            : this.ratedStyle()?.customColors;

        return custom === false ? NEUTRAL_COLOR : GRADUATED_COLORS;
    }

    /**
     * Couleur de chaque cran, dans l'ordre des crans : celle de la classe où le cran range le
     * répondant. Les couleurs sont celles de l'élément quand il en porte, celles que l'éditeur
     * applique d'office sinon — une question tout juste déposée n'en a pas encore.
     *
     * Le mode neutre ne se lit pas ici : ces couleurs restent celles de l'élément, c'est la carte
     * qui leur substitue celle des réponses quand l'échelle renonce à juger la note.
     */
    public stepColors(): string[] {
        switch (this.type) {
            case QUESTION_TYPE.NPS:
                return this.npsStepColors();
            case QUESTION_TYPE.CES:
                return this.effortColors();
            case QUESTION_TYPE.SCALE:
                // Les étoiles ne gradueraient rien : on les compte, on ne les lit pas.
                return this.ratedSteps().map(() => (this.isVisual() ? STAR_COLOR : RATING_COLOR));
            case QUESTION_TYPE.CSAT:
            case QUESTION_TYPE.MONO_CSAT:
            case QUESTION_TYPE.MULTI_CSAT:
                // Les étoiles ne gradueraient rien : on les compte, on ne les lit pas.
                return this.isVisual() && this.visualEmoji() === VISUAL_EMOJI.STAR
                    ? this.ratedSteps().map(() => STAR_COLOR)
                    : this.satisfactionColors();
            default:
                return [];
        }
    }

    /**
     * Le paramétrage de cet élément, son mode de couleurs réglé.
     *
     * Le mode gradué renseigne les couleurs en même temps qu'il les fait primer : le questionnaire
     * les applique telles quelles, il ne les devine pas. Le mode neutre les laisse en place et se
     * contente de les écarter — y revenir doit rendre à l'échelle les couleurs qu'elle avait, non
     * celles de l'éditeur.
     */
    public withColorMode(mode: RatedColorMode): ElementConfiguration {
        const graduated = mode === GRADUATED_COLORS;

        return this.isNps()
            ? this.withStyle({...this.npsColors(), customColor: graduated})
            : this.withStyle({colors: this.gradedColors(), customColors: graduated});
    }

    /**
     * Sous quelle forme la question se présente au répondant : des nombres, des icônes, des libellés.
     * Celle qui ne dit rien se note en nombres, la forme la plus courante.
     */
    public representation(): REPRESENTATION {
        return this.ratedStyle()?.type ?? REPRESENTATION.NUMBER;
    }

    /**
     * Formes que cette question peut prendre, telles que le paramétrage les propose. Un effort n'en
     * a que deux : ni l'étoile ni le smiley ne diraient la peine qu'il a fallu se donner.
     */
    public representations(): REPRESENTATION[] {
        if (this.isCes()) return CES_REPRESENTATIONS;

        return this.isScale() ? SCALE_REPRESENTATIONS : RATED_REPRESENTATIONS;
    }

    /** Vrai d'une question qui se note en icônes : ses crans se désignent, ils ne se lisent pas. */
    public isVisual(): boolean {
        return this.representation() === REPRESENTATION.VISUAL;
    }

    /** Vrai d'une question qui se note en toutes lettres : chaque cran porte alors son libellé. */
    public isTextual(): boolean {
        return this.representation() === REPRESENTATION.TEXT;
    }

    /**
     * Dessin répété d'un cran à l'autre : l'étoile, ou le smiley. Une question qui ne dit rien se
     * note en étoiles, la forme la plus attendue.
     */
    public visualEmoji(): VISUAL_EMOJI {
        return this.ratedStyle()?.visualSettings?.emoji === VISUAL_EMOJI.EMOJI ? VISUAL_EMOJI.EMOJI : VISUAL_EMOJI.STAR;
    }

    public withVisualEmoji(emoji: VISUAL_EMOJI): ElementConfiguration {
        return this.withStyle({visualSettings: {...this.ratedStyle()?.visualSettings, emoji}});
    }

    /**
     * Dessin de chaque cran, quand la question se note en icônes ; rien sinon. Les étoiles se
     * comptent, toutes semblables ; les smileys se lisent, du plus mécontent au plus satisfait.
     */
    public stepIcons(): string[] {
        const steps = this.ratedSteps();
        if (!this.isVisual()) return [];

        if (this.visualEmoji() === VISUAL_EMOJI.STAR) return steps.map(() => STAR_ICON);

        const smileys = SMILEY_PALETTES[steps.length];

        return smileys?.length === steps.length ? smileys : steps.map(() => NEUTRAL_SMILEY);
    }

    /**
     * Ce que porte chaque cran : son nombre, ou son libellé quand la question se note en toutes
     * lettres. Les libellés sont ceux de l'échelle retenue — l'élément porte ceux de chacune des
     * échelles qu'il peut proposer.
     *
     * Ils partent bruts : un modèle d'enquête les livre sous forme de clés de traduction, c'est à la
     * vue de les traduire. À défaut de libellés au compte des crans, la question retombe sur ses
     * nombres : mieux vaut une échelle chiffrée qu'une échelle muette.
     */
    public stepLabels(): string[] {
        const steps = this.ratedSteps();
        if (this.representation() !== REPRESENTATION.TEXT) return steps.map((step) => `${step}`);

        const texts = this.configuration?.buttonsTexts?.[this.buttonsTextsKey()] ?? [];

        return texts.length === steps.length
            ? texts.map((button) => button.text?.[LABEL_KEY] ?? '')
            : steps.map((step) => `${step}`);
    }

    /**
     * Le paramétrage de cet élément, sa représentation réglée.
     *
     * Une satisfaction qui quitte les nombres quitte aussi le zéro à dix : ni les icônes ni les
     * libellés ne vont au-delà de cinq crans — onze visages ne se distingueraient pas, et l'élément
     * ne porte de libellés que pour quatre et cinq. Elle est ramenée à cinq plutôt que laissée sur
     * une étendue que le paramétrage n'offre plus.
     *
     * Une échelle d'évaluation qui passe aux dessins reçoit autant d'étoiles que son dernier cran
     * valait : les deux formes partagent la même longueur, et l'utilisateur retrouve l'échelle qu'il
     * venait de régler plutôt qu'une échelle repartie de zéro.
     */
    public withRepresentation(representation: REPRESENTATION): ElementConfiguration {
        if (this.isScale() && representation === REPRESENTATION.VISUAL) {
            return this.withStyle({type: representation, ...this.scaleIconsPatch(this.scaleRange())});
        }

        const capped =
            this.isSatisfaction() &&
            representation !== REPRESENTATION.NUMBER &&
            this.csatRange() === CSAT_RANGE.ZERO_TO_TEN;

        return this.withStyle({
            type: representation,
            ...(capped ? this.csatRangePatch(CSAT_RANGE.ONE_TO_FIVE) : {}),
        });
    }

    /**
     * Premier cran d'une échelle d'évaluation notée en nombres : zéro ou un. Celle qui ne dit rien
     * part de un — on compte rarement à partir de zéro quand on note.
     *
     * Il ne vaut pas des dessins : une échelle d'icônes se note toujours depuis un, il n'y a pas
     * d'étoile qui vaille zéro. Il reste en place pour autant, et reprend au retour aux nombres.
     */
    public scaleStart(): number {
        return this.ratedStyle()?.numberSettings?.start ?? DEFAULT_SCALE_START;
    }

    /** Le paramétrage de cet élément, le premier cran de son échelle d'évaluation réglé. */
    public withScaleStart(start: number): ElementConfiguration {
        return this.withStyle({numberSettings: {...this.ratedStyle()?.numberSettings, start}});
    }

    /**
     * Longueur d'une échelle d'évaluation : son dernier cran quand elle se note en nombres, son
     * nombre d'icônes quand elle se note en dessins.
     *
     * Les deux formes rangent la leur de son côté, et c'est celle de la forme retenue qui compte.
     * Elles restent égales : passer aux dessins recopie le dernier cran en nombre d'étoiles, et
     * régler l'un règle l'autre.
     */
    public scaleRange(): number {
        const style = this.ratedStyle();
        const range = this.isVisual() ? style?.visualSettings?.range : style?.numberSettings?.range;

        return range ?? DEFAULT_SCALE_RANGE;
    }

    /**
     * Le paramétrage de cet élément, la longueur de son échelle d'évaluation réglée. Les deux formes
     * suivent ensemble : l'échelle est la même, seule la façon de la présenter change.
     */
    public withScaleRange(range: number): ElementConfiguration {
        return this.withStyle({
            numberSettings: {...this.ratedStyle()?.numberSettings, range},
            ...(this.isVisual() ? this.scaleIconsPatch(range) : {}),
        });
    }

    /**
     * Étendue sur laquelle une satisfaction se note, telle que le paramétrage la propose. Celle
     * qu'aucun réglage ne décrit va de zéro à dix, comme les autres indicateurs.
     */
    public csatRange(): CSAT_RANGE {
        const count = this.ratedStyle()?.numberSettings?.range ?? INDICATOR_STEP_COUNT;

        return CSAT_RANGES.find((range) => CSAT_RANGE_BOUNDS[range].count === count) ?? CSAT_RANGE.ZERO_TO_TEN;
    }

    /**
     * Le paramétrage de cet élément, l'étendue de sa satisfaction réglée.
     *
     * Les couleurs suivent la nouvelle étendue : elles vont une par cran, quatre crans n'ont que
     * faire des onze couleurs de la précédente. Celles que l'élément portait sont donc remplacées,
     * et non complétées.
     */
    public withCsatRange(range: CSAT_RANGE): ElementConfiguration {
        return this.withStyle(this.csatRangePatch(range));
    }

    /**
     * Étendue sur laquelle un effort se note en nombres, telle que le paramétrage la propose. Celui
     * qu'aucun réglage ne décrit va de zéro à dix, comme les autres indicateurs.
     *
     * Elle reste lue en toutes lettres, bien que la question ne s'y présente plus sur ses crans
     * chiffrés : elle n'est pas effacée pour autant, et reprend dès le retour aux nombres.
     */
    public cesRange(): CES_RANGE {
        const count = this.ratedStyle()?.numberSettings?.range ?? INDICATOR_STEP_COUNT;

        return CES_RANGES.find((range) => CES_RANGE_BOUNDS[range].count === count) ?? CES_RANGE.ZERO_TO_TEN;
    }

    /**
     * Le paramétrage de cet élément, l'étendue de son effort réglée.
     *
     * Les couleurs suivent la nouvelle étendue : elles vont une par cran, cinq crans n'ont que faire
     * des onze couleurs de la précédente. Celles que l'élément portait sont donc remplacées, et non
     * complétées.
     */
    public withCesRange(range: CES_RANGE): ElementConfiguration {
        const {start, count} = CES_RANGE_BOUNDS[range];

        return this.withStyle({
            numberSettings: {...this.ratedStyle()?.numberSettings, start, range: count},
            colors: effortPalette(count),
        });
    }

    /**
     * Échelle de libellés d'un effort noté en toutes lettres : par quels mots ses cinq encadrés se
     * disent. Celui qui ne dit rien parle de facilité, l'échelle la plus courante.
     */
    public cesTextRepresentation(): CES_TEXT_REPRESENTATION {
        const type = this.ratedStyle()?.textSettings?.typeRepresentation;

        return type && CES_TEXT_REPRESENTATIONS.includes(type) ? type : CES_TEXT_REPRESENTATION.EASY;
    }

    /**
     * Le paramétrage de cet élément, l'échelle de libellés de son effort réglée. Les libellés
     * eux-mêmes ne bougent pas : l'élément porte ceux de chaque échelle, et n'affiche que ceux de
     * l'échelle retenue — en changer, c'est changer de jeu, non le réécrire.
     */
    public withCesTextRepresentation(typeRepresentation: CES_TEXT_REPRESENTATION): ElementConfiguration {
        return this.withStyle({textSettings: {...this.ratedStyle()?.textSettings, typeRepresentation}});
    }

    /** Une zone de texte n'attend rien du répondant : elle lui donne à lire un message composé ici. */
    public isTextZone(): boolean {
        return this.type === CONTENT_TYPE.TEXT_ZONE;
    }

    /** Une invitation à déposer un avis renvoie le répondant vers la plateforme, sans rien lui demander. */
    public isPlatformReview(): boolean {
        return this.type === CONTENT_TYPE.PLATFORM_REVIEW;
    }

    /**
     * Plateforme visée. Celle qui n'en désigne aucune tombe dans le cas général : elle n'a pas de
     * logo propre et se présente sous le dessin générique.
     */
    public reviewSite(): TargetPlatformReview {
        return this.site && TARGET_PLATFORM_REVIEWS.includes(this.site) ? this.site : TargetPlatformReview.OTHER;
    }

    /**
     * Cet élément, sa plateforme changée — et le bouton refait à ses couleurs.
     *
     * Les couleurs suivent la plateforme parce que le bouton l'annonce : il porte son nom et doit se
     * reconnaître à sa livrée. Elles restent modifiables ensuite, l'enquête pouvant vouloir s'en
     * écarter ; c'est le changement de plateforme, et lui seul, qui les repose.
     */
    public withReviewSite(site: TargetPlatformReview): Partial<ElementPage> {
        const {fontColor, backgroundColor} = PLATFORM_REVIEW_BUTTONS[site];

        return {site, buttonFontColor: fontColor, buttonBackgroundColor: backgroundColor};
    }

    /**
     * Ce que le bouton d'invitation annonce : le nom de la plateforme visée, sauf dans le cas
     * général — nommer un site que l'enquête n'a pas désigné n'aurait pas de sens.
     *
     * C'est une clé de traduction : le libellé n'est pas une donnée de l'enquête mais une phrase de
     * l'application, qui suit donc la langue du répondant.
     */
    public reviewActionKey(): string {
        return `label.platform_review_action_${this.reviewSite().toLowerCase()}`;
    }

    /** Couleurs du bouton : celles de l'élément quand il en porte, celles de sa plateforme sinon. */
    public reviewButtonColors(): {fontColor: string; backgroundColor: string} {
        const {fontColor, backgroundColor} = PLATFORM_REVIEW_BUTTONS[this.reviewSite()];

        return {
            fontColor: this.buttonFontColor || fontColor,
            backgroundColor: this.buttonBackgroundColor || backgroundColor,
        };
    }

    /**
     * Réglages de l'invitation, tels que le paramétrage les compose. Le lien part vide tant qu'il
     * n'a pas été saisi : c'est ce qui le fait signaler comme manquant.
     */
    public reviewForm(): PlatformReviewForm {
        const {fontColor, backgroundColor} = this.reviewButtonColors();

        return {
            site: this.reviewSite(),
            link: this.link ?? '',
            buttonFontColor: fontColor,
            buttonBackgroundColor: backgroundColor,
            openNewTab: this.openNewTab ?? true,
        };
    }

    /**
     * Crans sur lesquels la question se note, du premier au dernier, l'un et l'autre compris.
     *
     * Une échelle d'évaluation part d'où son paramétrage le dit, zéro compris, et s'arrête où il le
     * dit — sauf en icônes, qui se notent toujours depuis un : il n'y a pas d'icône qui vaille zéro.
     * Sans paramétrage — une question tout juste déposée sur le canevas —, elle va de un à cinq.
     *
     * Une satisfaction se note sur l'étendue que son paramétrage propose, un effort de même — sauf
     * en toutes lettres, où ce sont ses cinq libellés qui font l'échelle. Une recommandation n'a pas
     * ce choix : son analyse impose le zéro à dix. Le reste ne se note pas.
     */
    public ratedSteps(): number[] {
        switch (this.type) {
            case QUESTION_TYPE.SCALE: {
                // Une échelle d'icônes se note toujours depuis un : il n'y a pas d'étoile qui vaille
                // zéro, et sa longueur se lit alors directement en nombre de dessins.
                const first = this.isVisual() ? 1 : this.scaleStart();

                return stepsFrom(first, this.scaleRange() - first + 1);
            }
            case QUESTION_TYPE.CSAT:
            case QUESTION_TYPE.MONO_CSAT:
            case QUESTION_TYPE.MULTI_CSAT: {
                const {start, count} = CSAT_RANGE_BOUNDS[this.csatRange()];

                return stepsFrom(start, count);
            }
            case QUESTION_TYPE.CES: {
                // Les libellés font l'échelle : ils sont cinq, quelle que soit l'étendue chiffrée,
                // qui reste en place et reprend dès le retour aux nombres.
                if (this.isTextual()) return stepsFrom(1, EFFORT_TEXT_STEP_COUNT);

                const {start, count} = CES_RANGE_BOUNDS[this.cesRange()];

                return stepsFrom(start, count);
            }
            case QUESTION_TYPE.NPS:
                return stepsFrom(0, INDICATOR_STEP_COUNT);
            default:
                return [];
        }
    }

    /**
     * Libellés des choix offerts, dans la langue de composition.
     *
     * Une question à choix en propose toujours au moins un : sans cela, elle s'ouvrirait sans rien à
     * saisir. Ce premier choix ne vit que sous les yeux de l'utilisateur — il n'entre dans le
     * brouillon qu'à la première frappe.
     */
    public choiceLabels(): string[] {
        const labels = listLabels(this.choices);

        return this.hasChoices() && labels.length === 0 ? [''] : labels;
    }

    /** Les choix, renommés : leurs identifiants et leurs autres traductions traversent. */
    public withChoiceLabels(labels: string[]): Choice[] {
        return withListLabels(this.choices, labels);
    }

    /**
     * Les choix, augmentés d'un nouveau, vide.
     *
     * Il entre dans l'enquête sans attendre d'être nommé, contrairement au premier choix d'une
     * question qui n'en portait aucun : c'est l'enregistrement qui lui vaudra son identifiant, et
     * une condition d'affichage ne peut viser qu'un choix qui en a un. Le faire attendre une frappe
     * le rendrait invisible aux conditions jusque-là.
     *
     * Les libellés déjà là repartent bruts, tels que l'enquête les porte : une clé de modèle ne doit
     * pas être remplacée par sa traduction au passage.
     */
    public withChoiceAdded(): Choice[] {
        return withListLabels(this.choices, [...this.choiceLabels(), '']);
    }

    /**
     * Les choix, celui du rang donné retiré.
     *
     * Le rang et non l'identifiant : un choix tout juste ajouté n'en a pas encore, et se retire
     * comme les autres. L'identifiant, lui, sert à retrouver les conditions qui l'observaient.
     */
    public withChoiceRemoved(rank: number): Choice[] {
        return (this.choices ?? []).filter((_, position) => position !== rank);
    }

    /**
     * Les choix, celui du rang donné déplacé à un autre.
     *
     * L'ordre est celui dans lequel le répondant lira les choix : le déplacement est une donnée de
     * l'enquête, pas un confort d'affichage. Les choix eux-mêmes traversent intacts — un identifiant
     * suit son choix, et les conditions qui l'observaient continuent de le viser.
     */
    public withChoiceMoved(from: number, to: number): Choice[] {
        const choices = [...(this.choices ?? [])];
        if (from === to || !choices[from] || !choices[to]) return choices;

        const [moved] = choices.splice(from, 1);
        choices.splice(to, 0, moved);

        return choices;
    }

    /**
     * Combien de choix le répondant peut désigner. Celle qui ne dit rien n'en attend qu'un, le cas
     * le plus courant — et celui que le serveur livre dans ses modèles.
     */
    public choicesAnswerType(): CHOICES_ANSWER_TYPE {
        return this.choicesStyle()?.type === CHOICES_ANSWER_TYPE.MULTIPLE
            ? CHOICES_ANSWER_TYPE.MULTIPLE
            : CHOICES_ANSWER_TYPE.UNIQUE;
    }

    public withChoicesAnswerType(type: CHOICES_ANSWER_TYPE): ElementConfiguration {
        return this.withChoicesStyle({type});
    }

    /** Sens dans lequel les choix se rangent ; celle qui ne dit rien les empile un par ligne. */
    public choicesLayout(): CHOICES_LAYOUT {
        return this.choicesStyle()?.layout === CHOICES_LAYOUT.HORIZONTAL
            ? CHOICES_LAYOUT.HORIZONTAL
            : CHOICES_LAYOUT.VERTICAL;
    }

    public withChoicesLayout(layout: CHOICES_LAYOUT): ElementConfiguration {
        return this.withChoicesStyle({layout});
    }

    /** Forme donnée aux choix ; celle qui ne dit rien les montre tous, en boutons. */
    public choicesDisplay(): CHOICES_DISPLAY {
        return this.choicesStyle()?.display === CHOICES_DISPLAY.LIST ? CHOICES_DISPLAY.LIST : CHOICES_DISPLAY.BUTTON;
    }

    public withChoicesDisplay(display: CHOICES_DISPLAY): ElementConfiguration {
        return this.withChoicesStyle({display});
    }

    /**
     * Vrai d'une question dont les choix s'effacent derrière une liste déroulante. Ils ne se rangent
     * plus alors ni en hauteur ni en largeur : la liste les découvre l'un sous l'autre au clic.
     */
    public hasChoicesDropdown(): boolean {
        return this.hasChoices() && this.choicesDisplay() === CHOICES_DISPLAY.LIST;
    }

    /** Libellés des critères d'une question multicritères, dans la langue de composition. */
    public criterionLabels(): string[] {
        return listLabels(this.subQuestions);
    }

    /** Les critères, renommés : leurs identifiants et leurs autres traductions traversent. */
    public withCriterionLabels(labels: string[]): SubQuestion[] {
        return withListLabels(this.subQuestions, labels);
    }

    /**
     * Les critères, augmentés d'un nouveau, vide.
     *
     * Il entre dans l'enquête sans attendre d'être nommé, comme un choix : c'est l'enregistrement qui
     * lui vaudra son identifiant, et le répondant note un critère par ligne — une ligne vide se
     * remplit, elle ne bloque rien.
     *
     * Les libellés déjà là repartent bruts, tels que l'enquête les porte : une clé de modèle ne doit
     * pas être remplacée par sa traduction au passage.
     */
    public withCriterionAdded(): SubQuestion[] {
        return withListLabels(this.subQuestions, [...this.criterionLabels(), '']);
    }

    /**
     * Les critères, celui du rang donné retiré.
     *
     * Le rang et non l'identifiant : un critère tout juste ajouté n'en a pas encore, et se retire
     * comme les autres. L'identifiant, lui, sert à retrouver les conditions qui l'observaient.
     */
    public withCriterionRemoved(rank: number): SubQuestion[] {
        return (this.subQuestions ?? []).filter((_, position) => position !== rank);
    }

    /**
     * Les critères, celui du rang donné déplacé à un autre.
     *
     * L'ordre est celui dans lequel le répondant notera les critères, une ligne par critère : le
     * déplacement est une donnée de l'enquête, pas un confort d'affichage. Les critères eux-mêmes
     * traversent intacts — un identifiant suit son critère, et les conditions qui l'observaient
     * continuent de le viser, comme les réponses déjà rangées sous lui.
     */
    public withCriterionMoved(from: number, to: number): SubQuestion[] {
        const subQuestions = [...(this.subQuestions ?? [])];
        if (from === to || !subQuestions[from] || !subQuestions[to]) return subQuestions;

        const [moved] = subQuestions.splice(from, 1);
        subQuestions.splice(to, 0, moved);

        return subQuestions;
    }

    /**
     * Copie de cet élément, prête à être posée dans une page.
     *
     * Copie en profondeur : le modèle du catalogue est partagé par tous les dépôts de son type, deux
     * questions posées ne doivent pas se retrouver liées par leur paramétrage.
     *
     * Tous les identifiants repartent à `null`, et non le seul identifiant de l'élément : ceux de ses
     * choix et de ses critères aussi. C'est un élément neuf, que le serveur nommera de bout en bout à
     * l'enregistrement — il refuse la création de tout ce qui se présente déjà nommé
     * (`ELEMENT_ID_FORBIDDEN`, `CHOICE_ID_FORBIDDEN`), et rejette alors le brouillon entier.
     *
     * Une zone de texte arrive vide : le message du modèle (« Saisir votre message ici ») n'est
     * qu'une invite à composer, que l'éditeur affiche déjà en placeholder. Recopié, il deviendrait
     * du contenu, et le répondant le lirait tel quel. Le message reste présent, vide : sans sa clé
     * `default`, le serveur le restitue en « UNKNOWN TRANSLATION » à l'aperçu comme au répondant.
     */
    public copyForPage(): ElementPage {
        const copy = JSON.parse(JSON.stringify(this)) as Plain<ElementPage>;

        return new ElementPage({
            ...copy,
            id: null,
            message: this.isTextZone() ? {[LABEL_KEY]: ''} : copy.message,
            choices: copy.choices?.map((choice) => ({...choice, id: null})),
            subQuestions: copy.subQuestions?.map((subQuestion) => ({...subQuestion, id: null})),
        });
    }

    /**
     * Cet élément, ses libellés de modèle remplacés par leur texte dans la langue de composition.
     *
     * Le catalogue livre les siens sous forme de clés de traduction. Posés tels quels dans le
     * brouillon, ils repartiraient en clés au serveur, qui ne connaîtrait de la question déposée
     * que `label.survey_template_question_nps_title` — illisible dans un export, et impossible à
     * retraduire hors de l'application. Le libellé est du contenu rédigé : il devient du texte dès
     * qu'il est posé, comme s'il avait été saisi.
     *
     * Rien à traduire, et c'est cette instance qui revient : l'appelant y lit que le libellé était
     * déjà du texte, sans avoir à le comparer lui-même.
     */
    public withTranslatedLabels(translate: LabelTranslator): ElementPage {
        const plain = JSON.parse(JSON.stringify(this)) as Plain<ElementPage>;
        const translated = resolveLabelKeys(plain, translate);

        return JSON.stringify(translated) === JSON.stringify(plain) ? this : new ElementPage(translated);
    }

    /**
     * Comment cet élément se présente aux sélecteurs d'une condition.
     *
     * Les libellés partent bruts : un modèle d'enquête les livre sous forme de clés de traduction,
     * c'est à la vue de les traduire.
     *
     * Rien n'est proposé tant que le serveur n'a pas attribué son identifiant : une condition
     * désigne sa cible par cet identifiant, la proposer plus tôt créerait une condition qui ne vise
     * rien. Elle réapparaît d'elle-même dès que le brouillon a été enregistré.
     *
     * Une question multicritères s'y décline en autant d'entrées que de critères — le répondant les
     * note séparément, une condition vise donc l'un d'eux et non la question entière —, et critère
     * par critère, la même règle vaut sur l'identifiant.
     */
    public conditionOptions(): ConditionQuestionOption[] {
        if (this.id === undefined || this.id === null) return [];

        const base = {
            questionId: this.id,
            title: this.title?.[LABEL_KEY] ?? '',
            type: this.type,
            comparison: this.conditionComparison(),
            values: this.conditionValues(),
        };

        if (this.type !== QUESTION_TYPE.MULTI_CSAT) {
            return [{...base, subQuestionId: null, criterion: ''}];
        }

        return (this.subQuestions ?? [])
            .filter((subQuestion) => subQuestion.id !== undefined && subQuestion.id !== null)
            .map((subQuestion) => ({
                ...base,
                subQuestionId: subQuestion.id as number,
                criterion: subQuestion.text?.[LABEL_KEY] ?? '',
            }));
    }

    /**
     * Les trois couleurs d'une recommandation, complétées de celles que l'éditeur applique d'office.
     *
     * Une couleur vide n'en est pas une : le serveur renvoie parfois `null`, et une question tout
     * juste déposée n'en porte aucune — l'échelle se colore malgré tout.
     */
    private npsColors(): typeof DEFAULT_NPS_COLORS {
        const npsColor = this.configuration?.style?.npsColor;

        return {
            negative: npsColor?.negative || DEFAULT_NPS_COLORS.negative,
            neutral: npsColor?.neutral || DEFAULT_NPS_COLORS.neutral,
            positive: npsColor?.positive || DEFAULT_NPS_COLORS.positive,
        };
    }

    /**
     * Couleur de chacun des crans d'une recommandation : celle de la classe où le cran range le
     * répondant — détracteur, passif, promoteur.
     */
    private npsStepColors(): string[] {
        const {negative, neutral, positive} = this.npsColors();

        return stepsFrom(0, INDICATOR_STEP_COUNT).map((step) => {
            if (step <= NPS_LAST_DETRACTOR_STEP) return negative;

            return step <= NPS_LAST_PASSIVE_STEP ? neutral : positive;
        });
    }

    /**
     * Ce qu'une échelle d'évaluation notée en dessins range de son côté : le dessin répété, et
     * combien de fois. L'étoile est le seul dessin qu'elle propose — un smiley jugerait la note, ce
     * qu'une échelle d'évaluation ne fait précisément pas.
     */
    private scaleIconsPatch(range: number): Record<string, unknown> {
        return {visualSettings: {...this.ratedStyle()?.visualSettings, emoji: VISUAL_EMOJI.STAR, range}};
    }

    /** Ce qu'une étendue change dans l'habillage : les bornes des crans, et leurs couleurs. */
    private csatRangePatch(range: CSAT_RANGE): Record<string, unknown> {
        const {start, count} = CSAT_RANGE_BOUNDS[range];

        return {
            numberSettings: {...this.ratedStyle()?.numberSettings, start, range: count},
            colors: satisfactionPalette(count),
        };
    }

    /**
     * Couleurs d'une satisfaction, une par cran.
     *
     * Celles de l'élément ne valent que si elles vont au nombre de ses crans : une échelle raccourcie
     * en traînerait sinon de la précédente, et une couleur manquerait à un cran raccroché. La palette
     * de l'éditeur prend alors le relais.
     */
    private satisfactionColors(): string[] {
        const steps = this.ratedSteps();
        const colors = this.ratedStyle()?.colors;

        return colors?.length === steps.length ? colors : satisfactionPalette(steps.length);
    }

    /** Couleurs d'un effort, une par cran, aux mêmes conditions que celles d'une satisfaction. */
    private effortColors(): string[] {
        const steps = this.ratedSteps();
        const colors = this.ratedStyle()?.colors;

        return colors?.length === steps.length ? colors : effortPalette(steps.length);
    }

    /**
     * Couleurs graduées de cette question, une par cran : celles que son indicateur donne à lire.
     * C'est ce que le mode gradué renseigne, le questionnaire les appliquant telles quelles.
     */
    private gradedColors(): string[] {
        return this.isCes() ? this.effortColors() : this.satisfactionColors();
    }

    /**
     * Sous quelle clé l'élément range les libellés de ses crans.
     *
     * Une satisfaction en porte un jeu par étendue, rangé au nombre de crans — c'est l'étendue qui
     * fait l'échelle. Un effort en porte un par échelle de libellés, rangé au nom de celle-ci : ses
     * cinq encadrés sont les mêmes quelle que soit l'étendue chiffrée, seuls les mots changent.
     */
    private buttonsTextsKey(): string {
        return this.isCes() ? this.cesTextRepresentation() : `${this.ratedSteps().length}`;
    }

    /**
     * Habillage d'une question à choix. Il ne passe pas par `ratedStyle` : une question à choix ne
     * se note pas, sa branche ne décrit ni forme d'échelle ni couleurs mais un rangement.
     */
    private choicesStyle(): ChoicesStyle | undefined {
        return this.configuration?.style?.choices;
    }

    /**
     * Le paramétrage de cet élément, sa branche de choix retouchée. Le reste de l'habillage
     * traverse, comme pour les questions notées.
     */
    private withChoicesStyle(patch: Partial<ChoicesStyle>): ElementConfiguration {
        const style = this.configuration?.style;

        return {...this.configuration, style: {...style, choices: {...style?.choices, ...patch}}};
    }

    /**
     * Habillage de cet élément, tel que son type le range : chaque type a sa branche, et le serveur
     * ne discrimine pas autrement. Une recommandation n'en fait pas partie — la sienne ne décrit que
     * des couleurs, sans forme ni bornes.
     */
    private ratedStyle(): RatingStyle | undefined {
        const key = this.styleKey();

        return key ? (this.configuration?.style?.[key] as RatingStyle | undefined) : undefined;
    }

    /** Branche de l'habillage où le type de cet élément range ce qui lui est propre. */
    private styleKey(): keyof ElementStyle | undefined {
        switch (this.type) {
            case QUESTION_TYPE.NPS:
                return 'npsColor';
            case QUESTION_TYPE.CES:
                return 'ces';
            case QUESTION_TYPE.SCALE:
                return 'scale';
            case QUESTION_TYPE.CSAT:
            case QUESTION_TYPE.MONO_CSAT:
            case QUESTION_TYPE.MULTI_CSAT:
                return 'csat';
            default:
                return undefined;
        }
    }

    /**
     * Le paramétrage de cet élément, sa branche d'habillage retouchée. Le reste de l'habillage
     * traverse : le serveur y range d'autres réglages que l'éditeur ne connaît pas encore.
     */
    private withStyle(patch: Record<string, unknown>): ElementConfiguration {
        const key = this.styleKey();
        if (!key) return {...this.configuration};

        const style = this.configuration?.style;

        return {
            ...this.configuration,
            style: {...style, [key]: {...(style?.[key] as object | undefined), ...patch}},
        };
    }

    /**
     * Comment une condition se compare à cette question : les comparaisons offertes, et les mots
     * pour les dire.
     *
     * Une échelle d'évaluation est la seule réponse qui s'ordonne : ses crans se suivent, « au moins
     * quatre » y a donc un sens, et ses comparaisons se lisent en nombres — « Égal », « Supérieur ».
     * Un statut d'indicateur ou un choix ne s'ordonnent pas — « après le détracteur » ne veut rien
     * dire —, ils ne se comparent que par égalité, et se lisent « Est », « N'est pas ».
     */
    private conditionComparison(): ConditionComparison {
        // Toute question conditionnable offre ses valeurs : c'est ce qui la rend conditionnable.
        return this.type === QUESTION_TYPE.SCALE
            ? {
                  operators: CONDITION_OPERATORS,
                  wording: COMPARISON_WORDING.NUMBER,
                  input: CONDITION_VALUE_INPUT.LIST,
              }
            : {
                  operators: EQUALITY_CONDITION_OPERATORS,
                  wording: COMPARISON_WORDING.STATE,
                  input: CONDITION_VALUE_INPUT.LIST,
              };
    }

    /**
     * Valeurs auxquelles la réponse à cette question peut être comparée.
     *
     * Un indicateur se compare à ses statuts d'analyse, non à la note brute : c'est ainsi que le
     * répondant est classé. Une échelle d'évaluation, qui ne mesure aucun indicateur, se compare à
     * ses crans. Une question à choix se compare à ses propres choix, désignés par leur identifiant.
     * Une question sans réponse énumérable n'offre rien.
     */
    private conditionValues(): ConditionValueOption[] {
        switch (this.type) {
            case QUESTION_TYPE.CSAT:
            case QUESTION_TYPE.MONO_CSAT:
            case QUESTION_TYPE.MULTI_CSAT:
                return statusValues('csat', ['UNSATISFIED', 'NEUTRAL', 'SATISFIED']);
            case QUESTION_TYPE.NPS:
                return statusValues('nps', ['DETRACTOR', 'NEUTRAL', 'PROMOTER']);
            case QUESTION_TYPE.CES:
                return statusValues('ces', ['HIGH', 'MEDIUM', 'LOW']);
            case QUESTION_TYPE.SCALE:
                // Le cran est sa propre étiquette : un nombre se lit dans toutes les langues.
                return this.ratedSteps().map((step) => ({value: `${step}`, label: `${step}`}));
            case QUESTION_TYPE.CHOICES:
                // Un choix pas encore enregistré n'a pas d'identifiant : rien ne pourrait le désigner.
                return (this.choices ?? [])
                    .filter((choice) => choice.id !== undefined && choice.id !== null)
                    .map((choice) => ({value: `${choice.id}`, label: choice.text?.[LABEL_KEY] ?? ''}));
            default:
                return [];
        }
    }
}
